// Notification Service - Browser Push Notifications for New Emails
// Gestisce le notifiche browser per nuove email

class NotificationService {
  constructor() {
    this.permission = 'default';
    this.lastCheck = new Date().toISOString();
    this.pollingInterval = null;
    this.enabled = false;
    this.checkIntervalMs = 60000; // Check ogni 60 secondi
  }

  // Inizializza il servizio
  async init() {
    if (!('Notification' in window)) {
      console.log('Browser non supporta le notifiche');
      return false;
    }

    this.permission = Notification.permission;
    
    // Carica preferenze salvate
    const saved = localStorage.getItem('notificationPrefs');
    if (saved) {
      const prefs = JSON.parse(saved);
      this.enabled = prefs.enabled || false;
      this.lastCheck = prefs.lastCheck || new Date().toISOString();
    }

    return true;
  }

  // Richiedi permesso notifiche
  async requestPermission() {
    if (!('Notification' in window)) {
      return { success: false, message: 'Browser non supportato' };
    }

    if (Notification.permission === 'granted') {
      this.permission = 'granted';
      return { success: true, message: 'Permesso già concesso' };
    }

    if (Notification.permission === 'denied') {
      return { success: false, message: 'Permesso negato. Abilitalo dalle impostazioni del browser.' };
    }

    try {
      const result = await Notification.requestPermission();
      this.permission = result;
      
      if (result === 'granted') {
        this.savePrefs();
        return { success: true, message: 'Notifiche abilitate!' };
      } else {
        return { success: false, message: 'Permesso non concesso' };
      }
    } catch (err) {
      return { success: false, message: err.message };
    }
  }

  // Mostra notifica browser
  showNotification(title, options = {}) {
    if (this.permission !== 'granted') {
      console.log('Permesso notifiche non concesso');
      return null;
    }

    const defaultOptions = {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'email-notification',
      requireInteraction: false,
      silent: false,
      ...options
    };

    try {
      const notification = new Notification(title, defaultOptions);
      
      notification.onclick = () => {
        window.focus();
        if (options.onClick) {
          options.onClick();
        } else {
          // Default: vai alla pagina PEC
          window.location.href = '/pec';
        }
        notification.close();
      };

      // Auto-close dopo 10 secondi
      setTimeout(() => notification.close(), 10000);
      
      return notification;
    } catch (err) {
      console.error('Errore notifica:', err);
      return null;
    }
  }

  // Notifica per nuove email
  notifyNewEmails(count, caselle = []) {
    if (count === 0) return;

    let body = '';
    if (caselle.length > 0) {
      const details = caselle
        .filter(c => c.non_lette > 0)
        .map(c => `${c.nome}: ${c.non_lette}`)
        .join('\n');
      body = details || `${count} nuovi messaggi`;
    } else {
      body = `Hai ${count} nuovi messaggi da leggere`;
    }

    // Determina il tipo prevalente per l'icona
    const pecCount = caselle.filter(c => c.tipo === 'pec').reduce((s, c) => s + c.non_lette, 0);
    const icon = pecCount > 0 ? '📧' : '✉️';

    this.showNotification(`${icon} ${count} Email Non Lette`, {
      body: body,
      tag: 'new-email-' + Date.now(),
      requireInteraction: count >= 5, // Richiedi interazione se molte email
    });
  }

  // Notifica singola email
  notifyEmail(email) {
    const tipoIcon = email.tipo_casella === 'pec' ? '🔴' : 
                     email.tipo_casella === 'standard' ? '🔵' : '🟢';
    
    this.showNotification(`${tipoIcon} Nuova Email`, {
      body: `${email.mittente_nome || email.mittente_email}\n${email.oggetto}`,
      tag: 'email-' + email.id,
    });
  }

  // Avvia polling per nuove email
  startPolling(api, onNewEmails) {
    if (this.pollingInterval) {
      this.stopPolling();
    }

    this.enabled = true;
    this.savePrefs();

    const checkNewEmails = async () => {
      try {
        const response = await api.get('/notifications/new-emails', {
          params: { since: this.lastCheck }
        });
        
        const data = response.data;
        
        if (data.new_count > 0) {
          // Aggiorna lastCheck
          this.lastCheck = data.check_time;
          this.savePrefs();
          
          // Mostra notifica
          this.notifyNewEmails(data.new_count, data.caselle);
          
          // Callback
          if (onNewEmails) {
            onNewEmails(data);
          }
        }
      } catch (err) {
        console.error('Errore check nuove email:', err);
      }
    };

    // Check immediato
    checkNewEmails();
    
    // Poi ogni intervallo
    this.pollingInterval = setInterval(checkNewEmails, this.checkIntervalMs);
    
    console.log('📧 Polling notifiche avviato');
  }

  // Ferma polling
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.enabled = false;
    this.savePrefs();
    console.log('📧 Polling notifiche fermato');
  }

  // Salva preferenze
  savePrefs() {
    localStorage.setItem('notificationPrefs', JSON.stringify({
      enabled: this.enabled,
      lastCheck: this.lastCheck,
      permission: this.permission
    }));
  }

  // Stato attuale
  getStatus() {
    return {
      supported: 'Notification' in window,
      permission: this.permission,
      enabled: this.enabled,
      polling: !!this.pollingInterval
    };
  }
}

// Singleton
const notificationService = new NotificationService();

export default notificationService;
